import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../Layout/Layout';

function PrivacyPolicy() {
    return (
        <Layout>
        <div className="policy-page-wrapper bg-blue-50">
            <div className="container mt-5 mb-5">
                <div className="row">
                    <div className="col-12">
                        <h1 className="mb-4">Privacy Policy</h1>
                        <p className="text-muted mb-4"><strong>Last Updated:</strong> April 2, 2026</p>

                        {/* Introduction */}
                        <section className="mb-5">
                            <h2 className="h4 mb-3">1. Introduction</h2>
                            <p>
                                Neuratrader ("we," "us," "our," or "Company") is committed to protecting your privacy.
                                This Privacy Policy explains how we collect, use, disclose, and safeguard your information
                                when you use our website and services. Please read this privacy policy carefully alongside our{' '}
                                <Link to="/terms-and-conditions" className="text-primary">Terms and Conditions</Link>. If you do
                                not agree with our policies and practices, please do not use our Services.
                            </p>
                        </section>

                        {/* Information We Collect */}
                        <section className="mb-5">
                            <h2 className="h4 mb-3">2. Information We Collect</h2>
                            
                            <h5 className="mt-4 mb-2">2.1 Information You Provide Directly</h5>
                            <ul>
                                <li><strong>Account Registration:</strong> Name, email address, password, phone number, and country</li>
                                <li><strong>Trading Information:</strong> Cryptocurrency addresses, API keys (encrypted), trading preferences, and account balances</li>
                                <li><strong>Communication:</strong> Messages, support tickets, feedback, and inquiries</li>
                                <li><strong>Payment Information:</strong> Transaction history and billing details (processed by third-party payment processors)</li>
                            </ul>

                            <h5 className="mt-4 mb-2">2.2 Information Collected Automatically</h5>
                            <ul>
                                <li><strong>Device Information:</strong> IP address, device type, operating system, browser type</li>
                                <li><strong>Usage Data:</strong> Pages visited, time spent, clicks, interactions, referral source</li>
                                <li><strong>Location Data:</strong> Approximate location based on IP address</li>
                                <li><strong>Cookies and Similar Technologies:</strong> See Cookie Policy for details</li>
                            </ul>

                            <h5 className="mt-4 mb-2">2.3 Information from Third Parties</h5>
                            <ul>
                                <li>Exchange APIs (Binance, Kraken, etc.) for trading data</li>
                                <li>Analytics providers for usage patterns</li>
                                <li>Compliance partners for KYC/AML verification</li>
                            </ul>
                        </section>

                        {/* How We Use Your Information */}
                        <section className="mb-5">
                            <h2 className="h4 mb-3">3. How We Use Your Information</h2>
                            <p>We use the information we collect to:</p>
                            <ul>
                                <li>Provide, maintain, and improve our Services</li>
                                <li>Process transactions and send related information</li>
                                <li>Send you promotional communications (with your consent)</li>
                                <li>Respond to your inquiries and provide customer support</li>
                                <li>Monitor and analyze trends, usage, and activities for service improvement</li>
                                <li>Detect, investigate, and prevent fraudulent transactions and other illegal activities</li>
                                <li>Comply with legal obligations and regulatory requirements</li>
                                <li>Personalize your experience and deliver tailored content</li>
                                <li>Enforce our <Link to="/terms-and-conditions" className="text-primary">Terms and Conditions</Link> and other agreements</li>
                            </ul>
                        </section>

                        {/* GDPR Compliance */}
                        <section className="mb-5">
                            <h2 className="h4 mb-3">4. GDPR Compliance (for EU Residents)</h2>
                            <p>
                                If you are located in the European Union, European Economic Area, or Switzerland,
                                the General Data Protection Regulation (GDPR) applies to our processing of your personal data.
                            </p>

                            <h5 className="mt-4 mb-2">4.1 Legal Basis for Processing</h5>
                            <p>We process your personal data based on the following legal bases:</p>
                            <ul>
                                <li><strong>Consent:</strong> When you explicitly agree to processing (e.g., marketing communications)</li>
                                <li><strong>Contract:</strong> To fulfill our obligations under our <Link to="/terms-and-conditions" className="text-primary">Terms and Conditions</Link></li>
                                <li><strong>Legal Obligation:</strong> To comply with applicable laws (KYC, AML, tax reporting)</li>
                                <li><strong>Legitimate Interests:</strong> To improve services, prevent fraud, and ensure security</li>
                            </ul>

                            <h5 className="mt-4 mb-2">4.2 Your GDPR Rights</h5>
                            <p>You have the right to:</p>
                            <ul>
                                <li><strong>Right of Access:</strong> Request a copy of the personal data we hold about you</li>
                                <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete data</li>
                                <li><strong>Right to Erasure:</strong> Request deletion of your data (subject to legal obligations)</li>
                                <li><strong>Right to Restrict Processing:</strong> Request that we limit how we use your data</li>
                                <li><strong>Right to Data Portability:</strong> Request your data in a structured, commonly used format</li>
                                <li><strong>Right to Object:</strong> Object to processing for marketing and profiling purposes</li>
                                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time without affecting lawfulness of prior processing</li>
                                <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your local data protection authority</li>
                            </ul>

                            <h5 className="mt-4 mb-2">4.3 Data Retention</h5>
                            <p>
                                We retain personal data for as long as necessary to provide our Services and fulfill the purposes
                                outlined in this Privacy Policy. However, we are required to maintain certain data for compliance with
                                financial and tax regulations (typically 5-7 years). You may request deletion subject to legal requirements.
                            </p>

                            <h5 className="mt-4 mb-2">4.4 International Data Transfers</h5>
                            <p>
                                Your information may be transferred to, stored in, and processed in countries other than your
                                country of residence. These countries may have data protection laws that differ from your home country.
                                When we transfer data internationally, we implement appropriate safeguards including:
                            </p>
                            <ul>
                                <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
                                <li>Binding Corporate Rules (BCRs)</li>
                                <li>Your explicit consent</li>
                            </ul>
                        </section>

                        {/* CCPA/CPRA Compliance */}
                        <section className="mb-5">
                            <h2 className="h4 mb-3">5. CCPA/CPRA Compliance (for California Residents)</h2>
                            <p>
                                If you are a California resident, the California Consumer Privacy Act (CCPA) and California Privacy
                                Rights Act (CPRA) provide you with specific rights regarding your personal information.
                            </p>

                            <h5 className="mt-4 mb-2">5.1 Your CCPA/CPRA Rights</h5>
                            <p>You have the right to:</p>
                            <ul>
                                <li><strong>Right to Know:</strong> Request what personal information we collect, use, share, and sell</li>
                                <li><strong>Right to Delete:</strong> Request deletion of personal information collected from you (with exceptions)</li>
                                <li><strong>Right to Opt-Out of Sales:</strong> Direct us not to sell or share your personal information</li>
                                <li><strong>Right to Opt-Out of Profiling:</strong> Opt-out of automated decision-making and profiling</li>
                                <li><strong>Right to Correct:</strong> Request correction of inaccurate personal information</li>
                                <li><strong>Right to Limit Use:</strong> Limit our use of sensitive personal information</li>
                                <li><strong>Right to Non-Discrimination:</strong> We cannot discriminate against you for exercising your rights</li>
                            </ul>

                            <h5 className="mt-4 mb-2">5.2 California Consumer Privacy Act (CCPA) - Categories of Personal Information</h5>
                            <p>We collect and use the following categories of personal information:</p>
                            <div className="table-responsive">
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>Category</th>
                                            <th>Examples</th>
                                            <th>Collected</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Identifiers</td>
                                            <td>Name, email, phone number, IP address</td>
                                            <td>Yes</td>
                                        </tr>
                                        <tr>
                                            <td>Commercial Information</td>
                                            <td>Trading history, account balances, purchases</td>
                                            <td>Yes</td>
                                        </tr>
                                        <tr>
                                            <td>Biometric Information</td>
                                            <td>Not collected</td>
                                            <td>No</td>
                                        </tr>
                                        <tr>
                                            <td>Internet Activity</td>
                                            <td>Browsing history, clicks, pages visited</td>
                                            <td>Yes</td>
                                        </tr>
                                        <tr>
                                            <td>Geolocation Data</td>
                                            <td>Approximate location based on IP</td>
                                            <td>Yes</td>
                                        </tr>
                                        <tr>
                                            <td>Sensory Information</td>
                                            <td>Not collected</td>
                                            <td>No</td>
                                        </tr>
                                        <tr>
                                            <td>Professional Information</td>
                                            <td>Trading experience, investment goals</td>
                                            <td>Yes</td>
                                        </tr>
                                        <tr>
                                            <td>Education Information</td>
                                            <td>Not collected</td>
                                            <td>No</td>
                                        </tr>
                                        <tr>
                                            <td>Sensitive Personal Information</td>
                                            <td>Financial account information, SSN/ID numbers</td>
                                            <td>No</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <h5 className="mt-4 mb-2">5.3 Do We Sell Personal Information?</h5>
                            <p>
                                We do not "sell" personal information as defined under CCPA in the traditional sense.
                                However, we may share personal information with service providers and partners for business purposes.
                                California residents have the right to opt-out of such sharing.
                            </p>

                            <h5 className="mt-4 mb-2">5.4 How to Exercise Your Rights</h5>
                            <p>To exercise your CCPA/CPRA rights, please contact us at:</p>
                            <ul>
                                <li><strong>Email:</strong> neuratrader@gmail.com</li>
                                {/* <li><strong>Mail:</strong> Auto Crypto Bot, Privacy Department, [Physical Address]</li>
                                <li><strong>Toll-Free Number:</strong> 1-XXX-XXX-XXXX</li> */}
                            </ul>
                            <p>
                                We will verify your request and respond within 45 days (extendable by 45 additional days if needed).
                                You may designate an authorized agent to make requests on your behalf.
                            </p>

                            <h5 className="mt-4 mb-2">5.5 Shine the Light Law</h5>
                            <p>
                                California Civil Code Section 1798.83 (Shine the Light Law) permits users who are California
                                residents to request and obtain specific information about what personal information we have shared
                                with third parties. To submit your request, please contact us using the information above.
                            </p>
                        </section>

                        {/* Data Security */}
                        <section className="mb-5">
                            <h2 className="h4 mb-3">6. Data Security</h2>
                            <p>
                                We implement appropriate technical and organizational security measures to protect your personal
                                information against unauthorized access, alteration, disclosure, or destruction. However, no method
                                of transmission over the Internet or electronic storage is completely secure. While we strive to
                                use commercially acceptable means to protect your information, we cannot guarantee absolute security.
                            </p>
                            <h5 className="mt-4 mb-2">Security Measures Include:</h5>
                            <ul>
                                <li>Encryption of sensitive data (SSL/TLS)</li>
                                <li>Encrypted API key storage</li>
                                <li>Two-factor authentication</li>
                                <li>Regular security audits and penetration testing</li>
                                <li>Access controls and authentication mechanisms</li>
                                <li>Secure password requirements</li>
                            </ul>
                        </section>

                        {/* Third-Party Sharing */}
                        <section className="mb-5">
                            <h2 className="h4 mb-3">7. Sharing Your Information</h2>
                            <p>We may share your personal information in the following circumstances:</p>
                            <ul>
                                <li><strong>Service Providers:</strong> Third parties who provide services on our behalf (hosting, analytics, payment processing)</li>
                                <li><strong>Cryptocurrency Exchanges:</strong> To execute trades with exchanges you authorize</li>
                                <li><strong>Legal Requirements:</strong> When required by law, court order, or regulatory authorities</li>
                                <li><strong>Business Transfers:</strong> In the event of merger, acquisition, or sale of assets</li>
                                <li><strong>Compliance Partners:</strong> KYC/AML verification providers as required by law</li>
                                <li><strong>Your Consent:</strong> When you explicitly authorize sharing</li>
                            </ul>
                        </section>

                        {/* Cookies and Tracking */}
                        <section className="mb-5">
                            <h2 className="h4 mb-3">8. Cookies and Similar Technologies</h2>
                            <p>
                                Please refer to our <a href="/cookie-policy" className="text-primary">Cookie Policy</a> for detailed
                                information about how we use cookies and similar tracking technologies.
                            </p>
                        </section>

                        {/* Children's Privacy */}
                        <section className="mb-5">
                            <h2 className="h4 mb-3">9. Children's Privacy</h2>
                            <p>
                                Our Services are not intended for children under 18 years of age. We do not knowingly collect
                                personal information from children under 18. If we become aware that we have collected information
                                from a child under 18, we will delete such information immediately. If you are aware of any such
                                collection, please contact us immediately.
                            </p>
                        </section>

                        {/* Your Privacy Choices */}
                        <section className="mb-5">
                            <h2 className="h4 mb-3">10. Your Privacy Choices</h2>
                            <h5 className="mt-4 mb-2">10.1 Marketing Communications</h5>
                            <p>
                                You may opt out of receiving promotional emails by clicking the unsubscribe link in our emails
                                or by adjusting your notification preferences in your account settings.
                            </p>

                            <h5 className="mt-4 mb-2">10.2 Cookie Management</h5>
                            <p>
                                You can control cookies through your browser settings. Disabling cookies may affect the functionality
                                of our Services. See our Cookie Policy for more information.
                            </p>

                            <h5 className="mt-4 mb-2">10.3 Do Not Track Signals</h5>
                            <p>
                                Some browsers include a "Do Not Track" feature. Our Services do not currently respond to Do Not Track
                                signals. However, you can use other privacy tools to limit data collection.
                            </p>
                        </section>

                        {/* Data Retention */}
                        <section className="mb-5">
                            <h2 className="h4 mb-3">11. Data Retention</h2>
                            <p>
                                We retain personal information for the period necessary to fulfill the purposes outlined in this
                                Privacy Policy. The retention period depends on the nature of the information and our legal obligations:
                            </p>
                            <ul>
                                <li><strong>Account Information:</strong> Retained while you maintain an account; deleted upon request unless legal obligations require retention</li>
                                <li><strong>Trading Data:</strong> Retained for 7 years to comply with financial regulations</li>
                                <li><strong>Communications:</strong> Retained for 3 years for support and dispute resolution</li>
                                <li><strong>Cookies:</strong> See Cookie Policy for specific retention periods</li>
                                <li><strong>Logs:</strong> Typically retained for 90 days for security purposes</li>
                            </ul>
                        </section>

                        {/* Third-Party Links */}
                        <section className="mb-5">
                            <h2 className="h4 mb-3">12. Third-Party Links</h2>
                            <p>
                                Our Services may contain links to third-party websites and services. We are not responsible for the
                                privacy practices of these third parties. We encourage you to review their privacy policies before
                                providing any personal information.
                            </p>
                        </section>

                        {/* Changes to This Policy */}
                        <section className="mb-5">
                            <h2 className="h4 mb-3">13. Changes to This Privacy Policy</h2>
                            <p>
                                We may update this Privacy Policy from time to time to reflect changes in our practices, technology,
                                legal requirements, or other factors. We will notify you of material changes by updating the "Last Updated"
                                date and, if required by law, by obtaining your consent.
                            </p>
                        </section>

                        {/* Contact Information */}
                        <section className="mb-5">
                            <h2 className="h4 mb-3">14. Contact Us</h2>
                            <p>If you have questions about this Privacy Policy or our privacy practices, please contact us:</p>
                            <ul>
                                <li><strong>Email:</strong> neuratrader@gmail.com</li>
                                {/* <li><strong>Support:</strong> <a href="https://t.me/CyanCastle" className="text-primary">Telegram</a></li>
                                <li><strong>Mail:</strong> Auto Crypto Bot, Privacy Department, [Physical Address]</li> */}
                            </ul>

                            {/* <h5 className="mt-4 mb-2">Data Protection Officer (GDPR)</h5>
                            <p>
                                For GDPR-related inquiries, you may contact our Data Protection Officer at:
                                <br />
                                <strong>Email:</strong> dpo@autcryptobot.com
                            </p> */}
                        </section>
                    </div>
                </div>
            </div>
        </div>
        </Layout>
    );
}

export default PrivacyPolicy;